import React, { useState, useMemo } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Download, Filter, Info } from 'lucide-react';
import { generateCompetitivePositioningData, formatCurrency, exportChart } from '../utils/visualization';
import type { FilterOptions, ExportConfig } from '../types/visualization';

interface CompetitivePositioningChartProps {
  filters?: FilterOptions;
  onFilterChange?: (filters: FilterOptions) => void;
  className?: string;
}

export const CompetitivePositioningChart: React.FC<CompetitivePositioningChartProps> = ({
  filters = {},
  onFilterChange,
  className = '',
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState<string | null>(null);

  const data = useMemo(() => generateCompetitivePositioningData(filters), [filters]);

  const handleExport = async (format: 'png' | 'csv') => {
    const chartElement = document.getElementById('competitive-positioning-chart');
    if (!chartElement) return;

    const config: ExportConfig = {
      format,
      filename: 'competitive-positioning-chart',
      includeData: true,
    };

    await exportChart(config, chartElement, data);
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <h3 className="font-semibold text-gray-900 mb-2">{data.companyName}</h3>
          <div className="space-y-1 text-sm">
            <p><span className="text-gray-600">Revenue:</span> {formatCurrency(data.x)}</p>
            <p><span className="text-gray-600">Valuation:</span> {formatCurrency(data.y)}</p>
            <p><span className="text-gray-600">GMV:</span> {formatCurrency(data.z)}</p>
            <p><span className="text-gray-600">Market Share:</span> {data.additionalData?.marketShare?.toFixed(1)}%</p>
            <p><span className="text-gray-600">Status:</span> {data.additionalData?.status}</p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`bg-white rounded-lg shadow-lg p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Competitive Positioning</h2>
          <p className="text-sm text-gray-600 mt-1">
            Revenue vs Valuation with bubble size representing GMV
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleExport('png')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              title="Export as PNG"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleExport('csv')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              title="Export as CSV"
            >
              <Download className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <select
                value={filters.company || ''}
                onChange={(e) => onFilterChange?.({ ...filters, company: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Companies</option>
                <option value="thumbtack">Thumbtack</option>
                <option value="taskrabbit">TaskRabbit</option>
                <option value="angi">Angi</option>
                <option value="handy">Handy</option>
                <option value="porch_group">Porch Group</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Metric Type</label>
              <select
                value={filters.metricType || ''}
                onChange={(e) => onFilterChange?.({ ...filters, metricType: e.target.value as any || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Metrics</option>
                <option value="revenue">Revenue</option>
                <option value="valuation">Valuation</option>
                <option value="gmv">GMV</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <select
                value={filters.year || ''}
                onChange={(e) => onFilterChange?.({ ...filters, year: e.target.value ? parseInt(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Years</option>
                <option value="2024">2024</option>
                <option value="2023">2023</option>
                <option value="2022">2022</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Chart */}
      <div id="competitive-positioning-chart" className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart
            data={data}
            margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              type="number"
              dataKey="x"
              name="Revenue"
              unit="M"
              tickFormatter={(value) => formatCurrency(value)}
              label={{ value: 'Revenue (Millions USD)', position: 'insideBottom', offset: -5 }}
            />
            <YAxis 
              type="number"
              dataKey="y"
              name="Valuation"
              unit="M"
              tickFormatter={(value) => formatCurrency(value)}
              label={{ value: 'Valuation (Millions USD)', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Scatter name="Companies" dataKey="y" fill="#8884d8">
              {data.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.color}
                  stroke={hoveredPoint === entry.companyId ? '#000' : entry.color}
                  strokeWidth={hoveredPoint === entry.companyId ? 2 : 0}
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Info className="h-4 w-4 text-gray-500" />
          <span className="text-sm text-gray-600">Bubble size represents GMV</span>
        </div>
        <div className="flex items-center gap-4">
          {data.map((company) => (
            <div 
              key={company.companyId}
              className="flex items-center gap-2"
              onMouseEnter={() => setHoveredPoint(company.companyId)}
              onMouseLeave={() => setHoveredPoint(null)}
            >
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: company.color }}
              />
              <span className="text-sm text-gray-700">{company.companyName}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};